import { useMemo } from 'react';
import { useAuthContext } from '../auth/useAuthContext';
import { Role, RoleGroupType, hasAnyRole } from '../auth/types/roles';

/**
 * useHasRole Hook
 *
 * Checks if the current authenticated user has a specific role or belongs to a role group.
 * Uses memoization for optimal performance.
 *
 * @example
 * ```typescript
 * // Single role check
 * const isManager = useHasRole(Role.MANAGER);
 *
 * // Multiple roles check (OR logic)
 * const canBook = useHasRole([Role.RECEPTIONIST, Role.CLINICIAN]);
 *
 * // Role group check
 * const isStaff = useHasRole(RoleGroup.ALL_STAFF);
 *
 * // Conditional rendering
 * function AdminPanel() {
 *   const isManager = useHasRole(Role.MANAGER);
 *
 *   if (!isManager) {
 *     return <AccessDenied />;
 *   }
 *
 *   return <Dashboard />;
 * }
 * ```
 */

// Overload signatures for better TypeScript inference
export function useHasRole(role: Role): boolean;
export function useHasRole(roles: readonly Role[]): boolean;
export function useHasRole(roleGroup: RoleGroupType): boolean;

/**
 * Implementation
 */
export function useHasRole(roleOrRoles: Role | readonly Role[] | RoleGroupType): boolean {
  const { user } = useAuthContext();

  return useMemo(() => {
    if (!user) return false;

    return hasAnyRole(user.roleId, roleOrRoles);
  }, [user, roleOrRoles]);
}

/**
 * useCurrentRole Hook
 *
 * Returns the current user's role ID, or null if not authenticated.
 *
 * @returns The current user's role ID or null
 *
 * @example
 * ```typescript
 * function UserBadge() {
 *   const role = useCurrentRole();
 *
 *   if (!role) return null;
 *
 *   return <Chip label={getRoleDisplayName(role)} />;
 * }
 * ```
 */
export function useCurrentRole(): Role | null {
  const { user } = useAuthContext();

  return useMemo(() => {
    return user?.roleId || null;
  }, [user]);
}

/**
 * useHasAnyRole Hook
 *
 * Checks if the current user has ANY of the specified roles.
 * Useful when you need explicit OR logic.
 *
 * @param roles - Array of roles to check
 * @returns True if user has any of the roles, false otherwise
 *
 * @example
 * ```typescript
 * function BookingSection() {
 *   const canBook = useHasAnyRole([Role.RECEPTIONIST, Role.MANAGER]);
 *
 *   if (!canBook) return null;
 *
 *   return <BookingForm />;
 * }
 * ```
 */
export function useHasAnyRole(roles: readonly Role[]): boolean {
  const { user } = useAuthContext();

  return useMemo(() => {
    if (!user) return false;

    return hasAnyRole(user.roleId, roles);
  }, [user, roles]);
}

/**
 * useHasAllRoles Hook
 *
 * Checks if the current user has ALL of the specified roles.
 * Note: Since users can only have one role, this typically returns true
 * only if a single role is specified and matches.
 *
 * @param roles - Array of required roles
 * @returns True if user has all roles, false otherwise
 *
 * @example
 * ```typescript
 * function SpecialFeature() {
 *   // This would only return true if the user is a manager
 *   const hasAccess = useHasAllRoles([Role.MANAGER]);
 *
 *   if (!hasAccess) return null;
 *
 *   return <SpecialFeatureContent />;
 * }
 * ```
 */
export function useHasAllRoles(roles: readonly Role[]): boolean {
  const { user } = useAuthContext();

  return useMemo(() => {
    if (!user) return false;

    // Since users can only have one role, check if it's in the list
    // and the list has only one item
    return roles.length === 1 && roles.includes(user.roleId);
  }, [user, roles]);
}

/**
 * useIsAuthenticated Hook
 *
 * Checks if the user is currently authenticated.
 * Convenience hook for checking authentication status.
 *
 * @returns True if user is authenticated, false otherwise
 *
 * @example
 * ```typescript
 * function NavBar() {
 *   const isAuthenticated = useIsAuthenticated();
 *
 *   return (
 *     <nav>
 *       {isAuthenticated ? <UserMenu /> : <LoginButton />}
 *     </nav>
 *   );
 * }
 * ```
 */
export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuthContext();

  return isAuthenticated;
}

/**
 * useAuthUser Hook
 *
 * Returns the current authenticated user or null.
 * Convenience hook for accessing user data.
 *
 * @returns The authenticated user object or null
 *
 * @example
 * ```typescript
 * function UserProfile() {
 *   const user = useAuthUser();
 *
 *   if (!user) return <LoginPrompt />;
 *
 *   return (
 *     <div>
 *       <h1>{user.firstName} {user.lastName}</h1>
 *       <p>{user.email}</p>
 *       <p>Role: {getRoleDisplayName(user.roleId)}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useAuthUser() {
  const { user } = useAuthContext();

  return user;
}
